import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ClipboardCheck, FileText, UsersRound } from 'lucide-react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, MetricCard, PageHeader, StatusBadge } from '../../components/ui';
import { assignmentGrade, byId, fullName, getCourse, getLessonsForVersion, userIsCourseTrainer } from '../../utils/lmsSelectors';

export default function TrainerDashboard() {
  const { data, currentUser } = useLms();
  const ownedCourses = data.courses.filter((course) => currentUser.role === 'admin' || userIsCourseTrainer(data, course, currentUser.id));
  const ownedCourseIds = ownedCourses.map((course) => course.id);
  const assignments = data.assignments.filter((assignment) => ownedCourseIds.includes(assignment.courseId));
  const pendingReviews = assignments.flatMap((assignment) => Object.entries(assignment.lessonProgress || {})
    .filter(([, progress]) => progress.status === 'needs_review')
    .map(([lessonId, progress]) => ({ assignment, lessonId, progress })));
  const published = ownedCourses.filter((course) => course.status === 'published').length;
  const drafts = ownedCourses.filter((course) => course.status === 'draft' || course.status === 'changes_requested').length;

  return (
    <>
      <PageHeader title="Trainer dashboard" description="Manage your course content, learner performance, and practical activity review queue." />
      <div className="metrics-grid">
        <MetricCard label="My courses" value={ownedCourses.length} helper="Owned or managed" icon={<BookOpen size={22} />} />
        <MetricCard label="Published" value={published} helper="Available for assignment" icon={<FileText size={22} />} />
        <MetricCard label="Draft / changes" value={drafts} helper="Need building or revisions" icon={<ClipboardCheck size={22} />} />
        <MetricCard label="Manual reviews" value={pendingReviews.length} helper="Pending practical approval" icon={<UsersRound size={22} />} />
      </div>
      <div className="grid two">
        <Card>
          <h2>Course status</h2>
          {ownedCourses.map((course) => (
            <div key={course.id} className="stacked-row">
              <div><Link className="text-link strong-link" to={`/trainer/courses/${course.id}/builder`}>{course.title}</Link><p className="muted small">{course.category}</p></div>
              <StatusBadge status={course.status} />
            </div>
          ))}
        </Card>
        <Card>
          <h2>Content workload</h2>
          {ownedCourses.map((course) => {
            const lessonCount = getLessonsForVersion(data, course.currentVersionId).length;
            return <div className="stacked-row" key={course.id}><Link className="text-link strong-link" to={`/trainer/courses/${course.id}/builder`}>{course.title}</Link><strong>{lessonCount} lessons</strong></div>;
          })}
        </Card>
      </div>
      <Card>
        <h2>Learner performance on my courses</h2>
        <DataTable
          rows={assignments.map((assignment) => ({
            id: assignment.id,
            learner: fullName(byId(data.users, assignment.assignedToUserId)),
            course: getCourse(data, assignment.courseId)?.title,
            courseId: assignment.courseId,
            status: assignment.status,
            grade: assignmentGrade(data, assignment)
          }))}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'course', label: 'Course', render: (row) => <Link className="text-link strong-link" to={`/trainer/courses/${row.courseId}/builder`}>{row.course}</Link> },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` }
          ]}
          emptyMessage="No learners assigned to your courses yet."
        />
      </Card>
    </>
  );
}
